/**
 * QBank Runtime Config Module
 *
 * Provides runtime product configuration for the unified single-build architecture.
 * The slug is extracted from the URL, and config is fetched from the API once on boot.
 *
 * IMPORTANT: Safari 12 / iOS 12 compatible — no optional chaining, no nullish coalescing.
 */

// In-memory config cache (populated by fetchConfig during boot)
var _config = null;
var _slug = null;
var _configFailed = false;
var _configError = null;

// Bootstrap aggregator slices ({init, aiCredits, collaborative}), populated only
// when the bootstrap path succeeds. Consumed by MainLayout/aiCredits/IndexPage so
// they can skip their own boot fetch. Null => those pages do their normal calls.
var _bootstrap = null;

/**
 * Whether to use the GET /api/quiz-app/bootstrap/{slug} aggregator instead of the
 * separate config/init/credit/collaborative calls. ENABLED BY DEFAULT. To disable
 * it for one browser (debugging), set localStorage 'qbank_use_bootstrap'='0'.
 * The server flag (config qbank.bootstrap_aggregate_enabled) is the real gate and
 * instant kill switch — if the server has it off, /bootstrap 404s and we fall back
 * to the original calls transparently.
 * Safari 12 safe (no optional chaining / nullish; localStorage wrapped in try).
 */
function bootstrapEnabled() {
  try {
    // Default ON; only the explicit per-browser opt-out '0' disables it.
    return !(window.localStorage && window.localStorage.getItem('qbank_use_bootstrap') === '0');
  } catch (e) {
    return true;
  }
}

/**
 * Try the bootstrap aggregator for a real product slug. Resolves to the config
 * object on success (and stashes the other slices in _bootstrap), or null on any
 * miss (404 = server flag off, network error, malformed body) so the caller can
 * fall back to the original per-call path.
 */
function fetchBootstrap(slug, isDev) {
  // The webview slice is platform-keyed (ios vs android; web is treated as ios on
  // the server, matching the standalone webview/all consumer). Detect android via
  // UA so the aggregate returns the correct native-controls slice.
  var platform = 'ios';
  try {
    if (typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent || '')) {
      platform = 'android';
    }
  } catch (e) { /* default ios */ }

  return fetch('/api/quiz-app/bootstrap/' + slug + '?platform=' + platform, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'Accept': 'application/json' },
  })
    .then(function (response) {
      if (!response.ok) {
        return null; // 404 (flag off) or error -> fall back to /config
      }
      return response.json();
    })
    .then(function (data) {
      if (!data || !data.config) {
        return null;
      }
      if (isDev) {
        // Dev keeps its constants-based _config base (qbank_source/publicPath from
        // constants.js); only pull ui_config from the aggregate, mirroring the dev
        // /config path. This lets `quasar dev` exercise the bootstrap endpoint.
        if (data.config.ui_config) {
          _config.ui_config = data.config.ui_config;
        }
      } else {
        _config = data.config;
      }
      _bootstrap = {
        init: data.init,
        aiCredits: data.aiCredits,
        collaborative: data.collaborative,
        notifications: data.notifications,
        webview: data.webview,
      };
      console.log('[qbank-config] Loaded via bootstrap aggregator for', slug, isDev ? '(dev)' : '');
      return _config;
    })
    .catch(function () {
      return null;
    });
}

/**
 * Extract the QBank slug from the current URL pathname.
 * URL pattern: /quizapps/{slug}/...
 * Returns the slug string or null if not matched.
 *
 * Dev mode (quasar dev): always returns null so the dev branch in fetchConfig()
 * reads the slug from constants.js. This is needed because in dev the router
 * base is `/quizapps/` (without a product slug), so URLs look like
 * /quizapps/qbanktestinterface where `qbanktestinterface` is a route name —
 * NOT a product slug. Without this guard the boot tries to fetch
 * /api/quiz-app/config/qbanktestinterface and 404s.
 */
function extractSlugFromUrl() {
  // process.env.NODE_ENV is inlined by webpack at build time.
  // In `quasar dev` it equals 'development'; in production builds it equals 'production'.
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
    return null;
  }

  var pathname = window.location.pathname;
  var match = pathname.match(/^\/quizapps\/([^/]+)/);
  if (match && match[1]) {
    return match[1];
  }
  return null;
}

/**
 * Get the Vue Router base path from the URL (sync, no API call needed).
 * Returns '/quizapps/{slug}/' for production, or the dev fallback.
 */
function getRouterBase() {
  var slug = extractSlugFromUrl();
  if (slug) {
    return '/quizapps/' + slug + '/';
  }
  // Dev mode fallback — use constants.js publicPath if available
  try {
    var CONSTANTS = require('../../constants.js');
    var publicPath = CONSTANTS.publicPath || '/quizapp/';
    // Ensure trailing slash
    if (publicPath.charAt(publicPath.length - 1) !== '/') {
      publicPath = publicPath + '/';
    }
    return publicPath;
  } catch (e) {
    return '/quizapp/';
  }
}

/**
 * Dev-mode standalone ui_config fetch (GET /api/quiz-app/config/{devSlug}).
 * Merges ui_config into the constants-based _config. Used as the default dev path
 * and as the dev bootstrap fallback. Behavior unchanged from before.
 */
function fetchDevUiConfig(devSlug) {
  return fetch('/api/quiz-app/config/' + devSlug, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'Accept': 'application/json' },
  })
    .then(function (response) {
      if (!response.ok) { return _config; }
      return response.json();
    })
    .then(function (data) {
      if (data && data.ui_config) {
        _config.ui_config = data.ui_config;
      }
      console.log('[qbank-config] Dev mode — loaded ui_config from API', _config);
      return _config;
    })
    .catch(function () {
      console.warn('[qbank-config] Dev mode — could not fetch ui_config from API, using defaults');
      return _config;
    });
}

/**
 * Fetch QBank config from the API. Called once during the qbank-config boot file.
 * Returns a Promise that resolves when config is loaded.
 */
function fetchConfig() {
  var slug = extractSlugFromUrl();

  if (!slug) {
    // Dev mode — load base config from constants.js, then fetch ui_config from API
    try {
      var CONSTANTS = require('../../constants.js');
      _config = {
        qbank_source: CONSTANTS.qbank_source,
        qBankName: CONSTANTS.qBankName,
        publicPath: CONSTANTS.publicPath,
        content_direction: CONSTANTS.content_direction || 'ltr',
      };
      _slug = 'dev';

      // Also fetch ui_config from API if dev_slug is configured
      var devSlug = CONSTANTS.qbank_app_slug;
      if (devSlug) {
        // Bootstrap aggregator path in dev too (opt-in) so it is testable via
        // `quasar dev`; falls back to the standalone ui_config fetch on any miss.
        if (bootstrapEnabled()) {
          return fetchBootstrap(devSlug, true).then(function (cfg) {
            if (cfg) {
              return cfg;
            }
            return fetchDevUiConfig(devSlug);
          });
        }
        return fetchDevUiConfig(devSlug);
      }

      return Promise.resolve(_config);
    } catch (e) {
      console.error('[qbank-config] No slug in URL and constants.js not available:', e);
      return Promise.reject(new Error('No QBank slug found in URL'));
    }
  }

  _slug = slug;

  // Bootstrap aggregator path (opt-in, default OFF — see bootstrapEnabled). One
  // request returns config+init+aiCredits+collaborative; on ANY miss we fall back
  // to the original per-call /config fetch with identical behavior. The await on
  // this Promise still gates the boot (so axios can stamp qbank_source from
  // _config before any other request fires) regardless of which path runs.
  if (bootstrapEnabled()) {
    return fetchBootstrap(slug).then(function (cfg) {
      if (cfg) {
        return cfg;
      }
      return fetchProductConfig(slug);
    });
  }

  return fetchProductConfig(slug);
}

/**
 * Original standalone config fetch (GET /api/quiz-app/config/{slug}). Used both as
 * the default path and as the bootstrap fallback. Behavior unchanged from before.
 */
function fetchProductConfig(slug) {
  return fetch('/api/quiz-app/config/' + slug, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'Accept': 'application/json',
    },
  })
    .then(function (response) {
      if (!response.ok) {
        throw new Error('QBank config not found for slug: ' + slug + ' (HTTP ' + response.status + ')');
      }
      return response.json();
    })
    .then(function (data) {
      _config = data;
      console.log('[qbank-config] Loaded config for', slug, data);
      return data;
    })
    .catch(function (error) {
      console.error('[qbank-config] Failed to load config:', error);
      // Don't re-throw — let boot sequence continue so Sentry initializes
      // and the app can show an error/retry UI instead of being stuck forever
      _configFailed = true;
      _configError = error;
      _config = {};
      return _config;
    });
}

/**
 * Get the loaded config (sync). Must be called after fetchConfig() resolves.
 * Returns the config object with qbank_source, qBankName, publicPath, content_direction.
 */
function getConfig() {
  if (!_config) {
    // Fallback: try loading from constants.js synchronously (dev mode)
    try {
      var CONSTANTS = require('../../constants.js');
      return CONSTANTS;
    } catch (e) {
      console.warn('[qbank-config] Config not loaded yet and constants.js not available');
      return {};
    }
  }
  return _config;
}

/**
 * Get the current slug.
 */
function getSlug() {
  return _slug;
}

/**
 * Get a UI config value using dot-notation key with a safe default.
 * Safari 12 compatible — no optional chaining, no nullish coalescing.
 *
 * Usage: getUiConfig('sidebar.show_fast_mode', false)
 *        getUiConfig('create_test.labels.subject', 'Select Subject')
 */
function getUiConfig(key, defaultValue) {
  if (!_config || !_config.ui_config) {
    return defaultValue;
  }
  var parts = key.split('.');
  var current = _config.ui_config;
  for (var i = 0; i < parts.length; i++) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return defaultValue;
    }
    current = current[parts[i]];
  }
  return (current !== null && current !== undefined) ? current : defaultValue;
}

module.exports = {
  extractSlugFromUrl: extractSlugFromUrl,
  getRouterBase: getRouterBase,
  fetchConfig: fetchConfig,
  getConfig: getConfig,
  getSlug: getSlug,
  getUiConfig: getUiConfig,
  isConfigFailed: function () { return _configFailed; },
  getConfigError: function () { return _configError; },
  // Bootstrap aggregator slices — null unless the bootstrap path succeeded.
  // Consumers (MainLayout/aiCredits/IndexPage) use these to skip their own boot
  // fetch; if null they fall back to their normal call.
  getBootstrapInit: function () { return _bootstrap ? _bootstrap.init : null; },
  getBootstrapAiCredits: function () { return _bootstrap ? _bootstrap.aiCredits : null; },
  getBootstrapCollaborative: function () { return _bootstrap ? _bootstrap.collaborative : null; },
  getBootstrapNotifications: function () { return _bootstrap ? _bootstrap.notifications : null; },
  getBootstrapWebview: function () { return _bootstrap ? _bootstrap.webview : null; },
};
